import React, { useEffect, useState, useCallback } from "react";
import { warehousesAPI } from "../../services/api";
import toast from "react-hot-toast";
import { ChevronDown, ChevronUp, Check, X } from "lucide-react";
import Pagination from "../../components/Pagination";
import { monthLabel } from "../../utils/month";

interface TicketItem { name: string; unit: string; expected: number; ok: boolean; missing: number; notes: string; }
interface Ticket {
  _id: string; roomName: string; createdAt: string;
  items: TicketItem[]; hygieneRating: string; qualityRating: string; overallNotes: string;
  images?: string[];
  inspectedBy?: { _id: string; name: string } | null;
}

const RATING: Record<string, { label: string; cls: string }> = {
  good: { label: "Tốt", cls: "bg-emerald-100 text-emerald-700" },
  ok:   { label: "Đạt", cls: "bg-yellow-100 text-yellow-700" },
  bad:  { label: "Kém", cls: "bg-red-100 text-red-600" },
};

function Th({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return <th className={`px-4 py-2.5 text-left text-xs font-semibold text-zinc-500 uppercase tracking-wide bg-zinc-50 ${className}`}>{children}</th>;
}
function Td({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return <td className={`px-4 py-3 text-sm text-zinc-700 ${className}`}>{children}</td>;
}

export default function TicketsPanel({ warehouseId, month }: { warehouseId: string; month: string }) {
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage]       = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [expanded, setExpanded] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const res = await warehousesAPI.getAllTickets(warehouseId, month); setTickets(res.data.data || []); }
    catch { toast.error("Lỗi tải phiếu kiểm"); }
    finally { setLoading(false); }
  }, [warehouseId, month]);

  useEffect(() => { load(); setPage(1); }, [load]);

  const paged = tickets.slice((page - 1) * pageSize, page * pageSize);

  return (
    <div className="space-y-3">
      <span className="text-sm font-bold text-zinc-800">{monthLabel(month)}</span>

      {loading ? (
        <p className="text-xs text-zinc-400 text-center py-3">Đang tải...</p>
      ) : (
        <div className="space-y-3">
          <div className="overflow-x-auto rounded-xl border border-zinc-200">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b border-zinc-200">
                  <Th>Ngày kiểm</Th>
                  <Th>Phòng</Th>
                  <Th>Người kiểm</Th>
                  <Th className="text-center">Kết quả</Th>
                  <Th className="text-center">Vệ sinh</Th>
                  <Th className="text-center">Chất lượng</Th>
                  <Th className="w-20 text-center">Chi tiết</Th>
                </tr>
              </thead>
              <tbody>
                {paged.length === 0 ? (
                  <tr><td colSpan={7} className="px-4 py-8 text-center text-sm text-zinc-400">Chưa có phiếu kiểm nào trong {monthLabel(month)}</td></tr>
                ) : paged.map(t => {
                  const missing = t.items.filter(i => !i.ok);
                  const bad     = missing.length > 0 || t.hygieneRating === "bad" || t.qualityRating === "bad";
                  const isOpen  = expanded === t._id;
                  return (
                    <React.Fragment key={t._id}>
                      <tr className={`border-b border-zinc-100 hover:bg-zinc-50 transition-colors ${bad ? "bg-red-50 hover:bg-red-50" : ""}`}>
                        <Td>{new Date(t.createdAt).toLocaleString("vi-VN", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}</Td>
                        <Td className="font-semibold text-zinc-800">{t.roomName}</Td>
                        <Td className="text-zinc-500">{t.inspectedBy?.name || "—"}</Td>
                        <Td className="text-center">
                          {bad
                            ? <span className="inline-flex items-center gap-1 text-xs font-semibold text-red-600 bg-red-100 px-2 py-0.5 rounded-full"><X size={10}/> {missing.length} thiếu</span>
                            : <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full"><Check size={10}/> Đủ</span>}
                        </Td>
                        <Td className="text-center">
                          {t.hygieneRating
                            ? <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${RATING[t.hygieneRating]?.cls}`}>{RATING[t.hygieneRating]?.label}</span>
                            : <span className="text-zinc-300 text-xs">—</span>}
                        </Td>
                        <Td className="text-center">
                          {t.qualityRating
                            ? <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${RATING[t.qualityRating]?.cls}`}>{RATING[t.qualityRating]?.label}</span>
                            : <span className="text-zinc-300 text-xs">—</span>}
                        </Td>
                        <Td className="text-center">
                          <button onClick={() => setExpanded(isOpen ? null : t._id)} className="p-1.5 text-zinc-400 hover:text-zinc-700 touch-manipulation">
                            {isOpen ? <ChevronUp size={15}/> : <ChevronDown size={15}/>}
                          </button>
                        </Td>
                      </tr>
                      {isOpen && (
                        <tr className="border-b border-zinc-100">
                          <td colSpan={7} className="px-6 py-3 bg-zinc-50">
                            <div className="space-y-1">
                              {t.items.map((item, i) => (
                                <div key={i} className={`flex items-center gap-2 text-sm ${!item.ok ? "text-red-600 font-medium" : "text-zinc-500"}`}>
                                  {item.ok ? <Check size={12} className="text-emerald-500 flex-shrink-0"/> : <X size={12} className="text-red-500 flex-shrink-0"/>}
                                  <span>{item.name} <span className="text-xs text-zinc-400">({item.expected} {item.unit})</span></span>
                                  {!item.ok && item.missing > 0 && <span className="text-xs font-bold text-red-500 ml-1">thiếu {item.missing}</span>}
                                  {item.notes && <span className="text-xs text-zinc-400 italic">· {item.notes}</span>}
                                </div>
                              ))}
                              {t.overallNotes && <p className="text-xs text-zinc-400 italic pt-2 border-t border-zinc-200 mt-2">{t.overallNotes}</p>}
                              {t.images && t.images.length > 0 && (
                                <div className="flex flex-wrap gap-2 pt-2 border-t border-zinc-200 mt-2">
                                  {t.images.map((url, i) => (
                                    <a key={i} href={url} target="_blank" rel="noopener noreferrer">
                                      <img src={url} alt={`Ảnh kiểm kho ${i}`} className="w-16 h-16 rounded-lg border border-zinc-200 object-cover hover:opacity-80" />
                                    </a>
                                  ))}
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
          <Pagination page={page} total={tickets.length} pageSize={pageSize} onChange={setPage} onPageSizeChange={s => { setPageSize(s); setPage(1); }} />
        </div>
      )}
    </div>
  );
}
